import React, { useState, useCallback, useRef } from 'react';

const FONT = '"Nunito", "Baloo 2", sans-serif';

/**
 * G3: Memory matching — 4 EN-VI pairs shown as 8 cards face-down.
 * Student flips 2 at a time to find matches.
 * Correctness = matched all pairs within a reasonable number of flips.
 */
export default function MemoryMatch({ roundData, onAnswer, palette: P, disabled }) {
  const pairs = roundData.prompt?.pairs || [];
  const totalPairs = pairs.length;

  // Build cards: each pair becomes 2 cards (EN and VI)
  const [cards] = useState(() => {
    const c = [];
    pairs.forEach((p, i) => {
      c.push({ id: `en_${i}`, text: p.word, pairIdx: i, type: 'en' });
      c.push({ id: `vi_${i}`, text: p.translation, pairIdx: i, type: 'vi' });
    });
    // Shuffle
    for (let i = c.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [c[i], c[j]] = [c[j], c[i]];
    }
    return c;
  });

  const [flipped, setFlipped] = useState(new Set());
  const [matched, setMatched] = useState(new Set());
  const [attempts, setAttempts] = useState(0);
  const [done, setDone] = useState(false);
  const lockRef = useRef(false);

  const handleFlip = useCallback((cardId) => {
    if (disabled || done || lockRef.current) return;
    if (flipped.has(cardId) || matched.has(cardId)) return;

    const newFlipped = new Set(flipped);
    newFlipped.add(cardId);
    setFlipped(newFlipped);

    if (newFlipped.size === 2) {
      lockRef.current = true;
      setAttempts(a => a + 1);
      const [id1, id2] = [...newFlipped];
      const card1 = cards.find(c => c.id === id1);
      const card2 = cards.find(c => c.id === id2);

      if (card1.pairIdx === card2.pairIdx && card1.type !== card2.type) {
        // Match!
        const newMatched = new Set(matched);
        newMatched.add(id1);
        newMatched.add(id2);
        setMatched(newMatched);
        setFlipped(new Set());
        lockRef.current = false;

        if (newMatched.size === cards.length) {
          setDone(true);
          // Good performance = fewer attempts than 2x pairs
          const isGood = (attempts + 1) <= totalPairs * 2;
          setTimeout(() => onAnswer(isGood, 'memory_complete'), 800);
        }
      } else {
        // No match — flip back after delay
        setTimeout(() => {
          setFlipped(new Set());
          lockRef.current = false;
        }, 800);
      }
    }
  }, [flipped, matched, cards, disabled, done, attempts, totalPairs, onAnswer]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, width: '100%', maxWidth: 480, margin: '0 auto', fontFamily: FONT }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: P.mutedText }}>
        Lật thẻ và ghép cặp EN ↔ VI
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, width: '100%' }}>
        {cards.map((card) => {
          const isFlipped = flipped.has(card.id) || matched.has(card.id);
          const isMatched = matched.has(card.id);
          return (
            <button key={card.id} onClick={() => handleFlip(card.id)}
              style={{
                aspectRatio: '1', borderRadius: 12,
                border: `2.5px solid ${isMatched ? '#2ECC71' : P.ink}`,
                boxShadow: `0 3px 0 ${isMatched ? '#27AE60' : P.ink}`,
                background: isFlipped ? (card.type === 'en' ? '#E8F4FD' : '#FFF3E0') : P.plum,
                color: isFlipped ? P.ink : 'transparent',
                fontSize: 14, fontWeight: 700, fontFamily: FONT,
                cursor: disabled || done ? 'default' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: 6, transition: 'all 0.2s',
              }}>
              {isFlipped ? card.text : '?'}
            </button>
          );
        })}
      </div>
      {done && (
        <div style={{ fontSize: 18, fontWeight: 800, color: '#2ECC71' }}>
          Hoàn thành! 🎉
        </div>
      )}
    </div>
  );
}
